import React, { useRef } from "react";
import { connect } from "react-redux";
import { Formik } from "formik";
import { Form } from "react-bootstrap";

import {
  TextInput,
  FormFilter,
  SubmitSearchButton,
  ClearFiltersButton,
  Select,
  DateInput,
  PercentageInput,
} from "components/forms";
import { fetchAccountsListChoices, fetchLeads, fetchLifeSettlementChoices, updateLeadsFilters } from "actions";
import { storedLeadsFiltersSelector, managementChoicesSelector } from "reducers";
import { LEAD_HEALTH_STATUS_OPTIONS, LEAD_ROLES_CHOICES, MARKETING_ACTIONS } from "./constants";
import { leadStageChoices, leadStatusChoices } from "../../constants";
import { makeChoices, prepareMultipleSelect } from "../../utils";
import NumericInput from "../../components/forms/NumericInput";
import { FaUser } from "react-icons/fa";

const LeadsFilterForm = ({ fetchLeads, fetchLifeSettlementChoices, currentFilters, ...props }) => {
  React.useEffect(() => {
    fetchLifeSettlementChoices();
    props.fetchAccountsListChoices();
  }, []);

  const handleStatusChange = selectedOptions => {
    if (selectedOptions) {
      const commaSeparatedStatusValues = selectedOptions.map(o => o.value).join(",");
      setStatus(commaSeparatedStatusValues);
    } else {
      setStatus(undefined);
    }
  };

  const handleStageChange = selectedOptions => {
    if (selectedOptions) {
      const commaSeparatedValues = selectedOptions.map(o => o.value).join(",");
      setStage(commaSeparatedValues);
    } else {
      setStage(undefined);
    }
  };

  let status_initial = prepareMultipleSelect("status", leadStatusChoices, currentFilters);
  let [status, setStatus] = React.useState();

  let stage_initial = prepareMultipleSelect("stage", leadStageChoices, currentFilters);
  let [stage, setStage] = React.useState();

  let initialValues = {
    last_name: currentFilters?.last_name || "",
    first_name: currentFilters?.first_name || "",
    phone: currentFilters?.phone || "",
    email: currentFilters?.email || "",
    insured_1_age: currentFilters?.insured_1_age || "",
    insured_1_health: currentFilters?.insured_1_health || "",
    lead_creation_start_date: currentFilters?.lead_creation_start_date || "",
    lead_creation_end_date: currentFilters?.lead_creation_end_date || "",
    sales_representative_id: currentFilters?.sales_representative_id || "",
    role: currentFilters?.role || "",
    source: currentFilters?.source || "",
    notes: currentFilters?.notes || "",
    stage: currentFilters?.stage || "",
    status: currentFilters?.status || "",
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        const filters = {
          ...values,
          status: status,
          stage: stage,
        };
        props.updateLeadsFilters(filters);
        await fetchLeads(filters);
        setSubmitting(false);
      }}
    >
      {({ handleSubmit, resetForm }) => {
        const statusRef = useRef();

        const resetCallback = () => {
          props.updateLeadsFilters({});
          statusRef.current.select?.clearValue();
          resetForm();
          fetchLeads();
        };

        return (
          <FormFilter title={"Leads Search"} onSubmit={handleSubmit}>
            <Form.Row>
              <TextInput label="Last Name" name="last_name" />
              <TextInput label="First Name" name="first_name" />
              <TextInput label="Phone" name="phone" />
              <TextInput label="Email" name="email" />
            </Form.Row>
            <Form.Row>
              <NumericInput label="Insured 1 Age (Min)" name="insured_1_age_min" />
              <Select label="Insured 1 Health" name="insured_1_health" options={LEAD_HEALTH_STATUS_OPTIONS} />
              <Select
                label="Lead Role"
                name="role"
                placeholder="Select Lead Role"
                options={LEAD_ROLES_CHOICES}
                md={3}
              />
              <Select
                label="Lead Source"
                name="marketing_action"
                placeholder="Select Lead Source"
                options={MARKETING_ACTIONS}
                md={3}
              />
            </Form.Row>
            <Form.Row>
              <DateInput label="Create Start Date" name="lead_creation_start_date" />
              <DateInput label="Create End Date" name="lead_creation_end_date" />
              <DateInput label="Last Edit Start Date" name="lead_last_edit_start_date" />
              <DateInput label="Last Edit End Date" name="lead_last_edit_start_date" />
            </Form.Row>
            <Form.Row>
              <PercentageInput label="Minimum Score" name="min_score" md={3} decimalLimit={1} />
              <PercentageInput label="Maximum Score" name="max_score" md={3} decimalLimit={1} />
              <DateInput label="Last Call Activity Start Date" name="last_call_activity_start_date" md={3} />
              <DateInput label="Last Call Activity End Date" name="last_call_activity_end_date" md={3} />
            </Form.Row>
            <Form.Row>
              <Select
                label="Sales Rep"
                name="sales_representative_id"
                placeholder="Select Sales Rep"
                options={props.salesRepChoices?.getChoices({ sortByLabel: true }) || []}
                icon={<FaUser />}
                md={3}
              />
              <Select
                label="Stage"
                name="stage"
                placeholder="Select Stage"
                isMulti={true}
                onChange={handleStageChange}
                options={leadStageChoices}
                defaultValue={stage_initial}
                md={3}
              />
              <Select
                label="Status"
                name="status"
                placeholder="Select Status"
                isMulti={true}
                onChange={handleStatusChange}
                options={leadStatusChoices}
                defaultValue={status_initial}
                md={3}
                submitRef={statusRef}
              />
            </Form.Row>
            <Form.Row>
              <TextInput
                label="Notes Search"
                name="notes_search"
                tooltip='Use "word1 word2" for exact search, * at the beginning as wildcard'
                md={3}
              />
              <Select
                label="Notes Search Type"
                name="notes_search_type"
                options={[
                  { value: "OR", label: "OR" },
                  { value: "AND", label: "AND" },
                ]}
                md={3}
              />
            </Form.Row>
            <SubmitSearchButton />
            <ClearFiltersButton onClick={resetCallback} />
          </FormFilter>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = state => {
  const choices = managementChoicesSelector(state);
  const currentFilters = storedLeadsFiltersSelector(state);
  let sourcingAgreementChoices = makeChoices(["None", "Active", "Inactive", "Pending"]);

  return {
    salesRepChoices: choices.case_sales_representatives,
    sourcingAgreementChoices,
    currentFilters,
  };
};

export default connect(mapStateToProps, {
  fetchLeads,
  updateLeadsFilters,
  fetchLifeSettlementChoices,
  fetchAccountsListChoices,
})(LeadsFilterForm);
